import { IDevice } from "../../Api/Interface/api.interface";

/**
 * Shared, admin-side formatting of device player/runtime metadata. Kept in one
 * place so the Devices list, Device detail and Monitoring page render the same
 * strings and empty-states ("Not reported" / "Unknown") rather than each
 * re-deriving them (and risking `undefined`/`0x0`/`null` leaking into the UI).
 *
 * The browser-side DETECTION of these values lives in
 * Pages/Player/deviceMeta.ts; this module only presents what the server stored.
 */

const NOT_REPORTED = "Not reported";

export type PlayerKind = "web" | "android" | "unknown";

export function playerKind(d: Pick<IDevice, "player_type">): PlayerKind {
  const t = (d.player_type || "").toLowerCase();
  if (t === "web") return "web";
  if (t === "android") return "android";
  return "unknown";
}

/** Short platform label for a chip, e.g. "WEB" / "ANDROID" / "—". */
export function playerTypeLabel(d: Pick<IDevice, "player_type">): string {
  const k = playerKind(d);
  if (k === "web") return "Web";
  if (k === "android") return "Android";
  return "—";
}

/** Browser Player version, stripped of the "web-" prefix the player reports. */
export function playerVersion(d: Pick<IDevice, "player_type" | "app_version">): string | null {
  if (!d.app_version) return null;
  if (playerKind(d) === "web") return d.app_version.replace(/^web-/i, "");
  return d.app_version;
}

export function browserLabel(
  d: Pick<IDevice, "browser" | "browser_version">,
): string {
  if (!d.browser) return "Unknown";
  return d.browser_version ? `${d.browser} ${d.browser_version.split(".")[0]}` : d.browser;
}

export function osLabel(d: Pick<IDevice, "os">): string {
  return d.os || "Unknown";
}

/** Compact one-line runtime summary for a table cell:
 *  web → "Windows · Edge 139", android → "Android app", else "". */
export function playerDetail(
  d: Pick<IDevice, "player_type" | "os" | "browser" | "browser_version" | "app_version">,
): string {
  const k = playerKind(d);
  if (k === "web") {
    const parts = [d.os, d.browser ? browserLabel(d) : null].filter(Boolean);
    return parts.join(" · ");
  }
  if (k === "android") {
    const v = playerVersion(d);
    return v ? `v${v}` : "Android app";
  }
  return "";
}

/** "1920 × 1080" or null when unknown (so callers can show "Not reported"). */
export function screenLabel(
  d: Pick<IDevice, "screen_width" | "screen_height">,
): string | null {
  if (d.screen_width && d.screen_height) return `${d.screen_width} × ${d.screen_height}`;
  return null;
}

export function orientationLabel(d: Pick<IDevice, "orientation">): string {
  if (d.orientation === "landscape") return "Landscape";
  if (d.orientation === "portrait") return "Portrait";
  return NOT_REPORTED;
}

export function pixelRatioLabel(d: Pick<IDevice, "device_pixel_ratio">): string {
  return d.device_pixel_ratio ? `${d.device_pixel_ratio}×` : NOT_REPORTED;
}

export { NOT_REPORTED };
